import { useCallback, useEffect, useRef, type RefCallback } from 'react';

/**
 * Horizontal overflow affordance (design audit 1-6 / 1-8): marks a scroll container with
 * `data-fade-start` / `data-fade-end` while content is hidden on that side, which the
 * `.scroll-fade` CSS turns into an edge mask, so cut-off tabs or columns read as "scroll for more"
 * instead of missing. With `revealActive`, the active item (`[data-state=active]` or
 * `[aria-current=page]`) is scrolled into view on mount.
 */
export function useScrollFade<T extends HTMLElement>(
  opts: { revealActive?: boolean } = {},
): RefCallback<T> {
  const cleanup = useRef<(() => void) | null>(null);
  const reveal = opts.revealActive ?? false;

  const ref = useCallback(
    (el: T | null) => {
      cleanup.current?.();
      cleanup.current = null;
      if (!el) return;
      const update = () => {
        const max = el.scrollWidth - el.clientWidth;
        el.toggleAttribute('data-fade-start', max > 1 && el.scrollLeft > 1);
        el.toggleAttribute('data-fade-end', max > 1 && el.scrollLeft < max - 1);
      };
      if (reveal) {
        const active = el.querySelector<HTMLElement>(
          '[data-state="active"], [aria-current="page"]',
        );
        if (active && el.scrollWidth > el.clientWidth) {
          // Only when the active item is (partly) hidden: a visible one keeps the bar at its start.
          const left = active.getBoundingClientRect().left - el.getBoundingClientRect().left;
          const right = left + active.offsetWidth;
          if (left < 0 || right > el.clientWidth) {
            const target = el.scrollLeft + left - (el.clientWidth - active.offsetWidth) / 2;
            el.scrollLeft = Math.max(0, target);
          }
        }
      }
      update();
      el.addEventListener('scroll', update, { passive: true });
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
      ro?.observe(el);
      if (el.firstElementChild) ro?.observe(el.firstElementChild);
      cleanup.current = () => {
        el.removeEventListener('scroll', update);
        ro?.disconnect();
      };
    },
    [reveal],
  );

  useEffect(() => () => cleanup.current?.(), []);
  return ref;
}
